import { ReactElement } from 'react';
import Slider from '../../../../new-components/Slider';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { Banner } from '../../dtos';
import ImageWrapper from '../../../../new-components/ImageWrapper';
import { logEvent } from '../../../../new-lib/analytics';
import Link from 'next/link';

interface BannerProps {
  banners: Banner[];
}

const DynamicBanners = ({ banners }: BannerProps) => {
  const isMobile = useSelector((state: any) => state.app.isMobile);

  const getPathFromUrl = (url: string): string => {
    try {
      const parsedUrl = new URL(url);
      return parsedUrl.pathname + parsedUrl.search + parsedUrl.hash;
    } catch (error) {
      return url;
    }
  };

  const handleClick = (banner: Banner) => {
    logEvent.click('banner_click', 'homepage', 'banner', {
      banner_type: banner.name
    });
  };

  const getBanners = () => {
    return banners
      ?.filter(banner => banner.redirectUrl && banner.redirectUrl !== '')
      .map((banner, index) => (
        <BannerContainer
          key={banner.id}
          onMouseOver={e => {
            e.currentTarget.style.transform = 'scale(1.01)';
          }}
          onMouseOut={e => {
            e.currentTarget.style.transform = 'scale(1)';
          }}
        >
          <Link
            key={banner.id}
            href={
              banner.newTab
                ? banner.redirectUrl
                : getPathFromUrl(banner.redirectUrl)
            }
            target={banner.newTab ? '_blank' : undefined}
            prefetch={!banner.newTab}
            onClick={() => handleClick(banner)}
            rel={banner.newTab ? 'noopener noreferrer' : undefined}
            passHref
          >
            <ImageWrapper
              src={banner.imageUrl}
              alt={banner.name}
              style={{ borderRadius: '8px' }}
              fill
              priority={index < 3}
              imageSize={'high'}
              withSkeleton
            />
          </Link>
        </BannerContainer>
      )) as ReactElement[];
  };

  const homepageBanners = {
    width: '542px',
    height: '188px',
    slidesPerView: 'auto' as 'auto',
    spaceBetweenSlides: 16,
    slidesPerGroup: 1,
    children: getBanners()
  };

  return isMobile ? (
    <div className="container-fluid">
      <HomeWrapper className="banners-container">
        <div className="banners-inner-container">
          {banners
            ?.filter(banner => banner.redirectUrl && banner.redirectUrl !== '')
            .map((banner, index) => (
              <Link
                key={banner.id}
                href={
                  banner.newTab
                    ? banner.redirectUrl
                    : getPathFromUrl(banner.redirectUrl)
                }
                target={banner.newTab ? '_blank' : undefined}
                prefetch={!banner.newTab}
                onClick={() => handleClick(banner)}
                rel={banner.newTab ? 'noopener noreferrer' : undefined}
                passHref
              >
                <ImageWrapper
                  src={banner.appImageUrl || banner.imageUrl}
                  alt={banner.name}
                  width={260}
                  height={130}
                  priority={index < 3}
                  style={{ borderRadius: '8px' }}
                  withSkeleton
                />
              </Link>
            ))}
        </div>
      </HomeWrapper>
    </div>
  ) : (
    <HomeWrapper className="container-fluid banners-container">
      <Container>
        <Slider {...homepageBanners} />
      </Container>
    </HomeWrapper>
  );
};

const Container = styled.div`
  .swiper-slide img {
    width: unset;
    height: unset;
    object-fit: unset;
  }

  img {
    aspect-ratio: unset !important;
  }
`;
const HomeWrapper = styled.div<any>`
  &.banners-container {
    height: auto;
    gap: 16px;
    display: flex;
    flex-direction: row;
    overflow-x: auto;
    overflow-y: hidden;
    -ms-overflow-style: none;
    scrollbar-width: none;
    > div,
    a {
      min-width: 542px;
      height: 188px;
    }
  }
  @media screen and (max-width: ${props =>
      props.theme.breakPoints.mobile.max}) {
    &.banners-container {
      height: 130px;
      overflow-x: auto;
      -ms-overflow-style: none;
      scrollbar-width: none;
      ::-webkit-scrollbar {
        display: none;
      }

      .banners-inner-container {
        height: 100%;
        width: max-content;
        display: flex;
        flex-direction: row;
        gap: 8px;
        ::-webkit-scrollbar {
          display: none;
        }

        > div,
        a {
          min-width: 260px;
          height: 130px;
        }
      }
    }
  }
`;

const BannerContainer = styled.div`
  padding: 24px;
  width: 100%;
  height: 100%;
  cursor: pointer;
`;

export default DynamicBanners;
